import { Button, InputNumber, Layout, notification, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';

import {
    FacilityDto,
    ProfileWithAddresses,
    SpecialtyDto,
} from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { DisplayValue } from '../display-value';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';
import { FetchRecordTable, FilterSelect } from '../table';

const { Text } = Typography;

export const OrientationMatches: FC = () => {
    const api = useApiClient();
    const [facilities, setFacilities] = useState<FacilityDto[]>([]);
    const [specialty, setSpecialties] = useState<SpecialtyDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const [facilitiesResponse, specialtiesResponse] = await Promise.all(
                [
                    api!.facilityControllerGetFacilities(),
                    api!.specialtyControllerGetSpecialties(),
                ]
            );

            setFacilities(facilitiesResponse);
            setSpecialties(specialtiesResponse);
        } catch (err) {
            notification.error({ message: unwrapError(err), duration: 3 });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!api?.isAuthenticated) return;

        void fetchData();
    }, [api?.isAuthenticated]);

    if (!api || isLoading) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Orientation Matches Preview</h2>
            </Header>
            <Content>
                <FetchRecordTable
                    bordered
                    size="small"
                    pagination={false}
                    filters={{
                        filters: {
                            facilityId: {
                                label: 'Facility',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Select Facility"
                                        showSearch
                                        optionFilterProp="label"
                                        options={facilities.map((facility) => ({
                                            label: facility.name,
                                            value: facility.id,
                                        }))}
                                    />
                                ),
                                displayValue: (facilityId: string) => {
                                    const facility = facilities.find(
                                        (f) => f.id === facilityId
                                    );
                                    return facility?.name || facilityId;
                                },
                            },
                            specialtyCode: {
                                label: 'Speciality',
                                control: ({ onApply, onCancel }) => (
                                    <FilterSelect
                                        onApply={onApply}
                                        onCancel={onCancel}
                                        style={{ width: 180 }}
                                        placeholder="Select Specialty"
                                        showSearch
                                        optionFilterProp="label"
                                        options={specialty.map((facility) => ({
                                            label: facility.name,
                                            value: facility.code,
                                        }))}
                                    />
                                ),
                                displayValue: (specialtyCode: string) => {
                                    const spec = specialty.find(
                                        (s) => s.code === specialtyCode
                                    );
                                    return spec?.name || specialtyCode;
                                },
                            },
                            stipendMinimumDistance: {
                                label: 'Minimum Stipend Distance (miles)', // TODO: make sure this label shows up
                                control: ({ onApply }) => (
                                    <InputNumber
                                        onBlur={(e) => onApply(e.target.value)}
                                    />
                                ),
                            },
                        },
                    }}
                    getRecords={(filters) =>
                        filters.facilityId
                            ? api.facilityControllerPreviewOrientationMatches({
                                  ...filters,
                                  stipendMinimumDistance:
                                      filters.stipendMinimumDistance || 0,
                              })
                            : Promise.resolve([])
                    }
                    cacheKey={'facilityControllerPreviewOrientationMatches'}
                    columns={[
                        {
                            dataIndex: 'nurseProfile',
                            title: 'Nurse',
                            render: (nurseProfile: ProfileWithAddresses) => (
                                <Button
                                    type="link"
                                    target="_blank"
                                    href={`/profiles/${nurseProfile?.id}`}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    {nurseProfile.firstName}{' '}
                                    {nurseProfile.lastName}
                                </Button>
                            ),
                        },
                        {
                            dataIndex: 'nurseProfile',
                            title: 'Location',
                            render: (nurseProfile: ProfileWithAddresses) => (
                                <Text>
                                    {nurseProfile.addresses?.length
                                        ? `${nurseProfile.addresses?.[0].city}, ${nurseProfile.addresses?.[0].state}`
                                        : `N/A`}
                                </Text>
                            ),
                        },
                        {
                            dataIndex: 'distanceMiles',
                            title: 'Distance (mi)',
                            render: (distanceMiles?: number) => (
                                <Text>
                                    {distanceMiles == null
                                        ? 'N/A'
                                        : distanceMiles.toLocaleString()}
                                </Text>
                            ),
                        },
                        {
                            dataIndex: 'nurseProfile',
                            title: 'Specialties',
                            render: (nurseProfile: ProfileWithAddresses) => (
                                <Text>
                                    {nurseProfile.specialties.map(
                                        (s: string) => (
                                            <>
                                                {s}
                                                <br />
                                            </>
                                        )
                                    )}
                                </Text>
                            ),
                        },
                        {
                            dataIndex: 'isQualifiedForStipend',
                            title: 'Would Qualify for Stipend',
                            render: (val) => <DisplayValue value={val} />,
                        },
                    ]}
                />
            </Content>
        </Layout>
    );
};
